import { useState } from 'react'
import {
  ChevronDown,
  HelpCircle,
  Users,
  Zap,
} from 'lucide-react'
import { decodeHtmlEntities, QUESTION_TYPE_LABELS } from './liveControlsUtils'

// ── Questions Section ─────────────────────────────────────────

function QuestionsSection({ liveQuestions, audienceMode = 'internal' }) {
  const [answeredExpanded, setAnsweredExpanded] = useState(true)

  // Auto-filter based on meeting audience mode — no manual toggle needed
  const filtered = liveQuestions.filter((q) =>
    audienceMode === 'external' ? q.askedByScope !== 'internal_team' : q.askedByScope === 'internal_team'
  )

  const open = filtered.filter((q) => q.status === 'open' || q.status === 'partially_answered')
  const answered = filtered.filter((q) => q.status === 'answered')

  return (
    <section className="rounded-2xl border border-primary-200/80 bg-primary-50/40 p-5 shadow-sm">
      <div className="flex items-center gap-2.5 mb-3.5">
        <HelpCircle className="h-5 w-5 text-primary-500" />
        <h4 className="text-base font-bold text-slate-900">Questions Captured</h4>
        {open.length > 0 ? (
          <span className="text-xs font-medium text-amber-600">{open.length} open</span>
        ) : null}
      </div>

      {/* Open questions */}
      {open.length > 0 ? (
        <div className="space-y-2.5">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
            Open ({open.length})
          </p>
          <div className="max-h-[20rem] overflow-y-auto space-y-2.5 pr-0.5">
            {open.map((q) => (
              <div key={q.questionId} className="rounded-lg border border-amber-200 bg-white px-3 py-2.5">
                <p className="text-sm leading-5 text-slate-900">{decodeHtmlEntities(q.questionText)}</p>
                <div className="flex items-center gap-2 mt-1">
                  {q.askedBy?.displayName ? (
                    <span className="text-xs text-slate-400">{q.askedBy.displayName}</span>
                  ) : null}
                  {q.questionType && q.questionType !== 'information_seeking' && QUESTION_TYPE_LABELS[q.questionType] ? (
                    <span className="text-xs font-medium text-primary-400">{QUESTION_TYPE_LABELS[q.questionType]}</span>
                  ) : null}
                  {q.refinementStatus === 'pending' ? (
                    <span
                      className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500"
                      title="Heuristic capture — waiting for LLM verification"
                    >
                      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-slate-400" />
                      verifying
                    </span>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <p className="text-sm text-slate-400">No open questions</p>
      )}

      {/* Answered questions — collapsed */}
      {answered.length > 0 ? (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setAnsweredExpanded((v) => !v)}
            className="flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700 transition-colors"
          >
            <ChevronDown className={`h-3 w-3 transition-transform ${answeredExpanded ? 'rotate-180' : ''}`} />
            Answered ({answered.length})
          </button>
          {answeredExpanded ? (
            <div className="mt-2.5 max-h-[16rem] overflow-y-auto space-y-2.5 pr-0.5">
              {answered.map((q) => (
                <div key={q.questionId} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
                  <p className="text-sm leading-5 text-slate-700">{decodeHtmlEntities(q.questionText)}</p>
                  {q.answerSummary ? (
                    <p className="mt-1 text-xs leading-4 text-slate-500 italic">{decodeHtmlEntities(q.answerSummary)}</p>
                  ) : null}
                  {q.answeredBy?.displayName ? (
                    <p className="mt-1 text-xs text-slate-400">{q.answeredBy.displayName}</p>
                  ) : null}
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  )
}

// ── Live Outputs Feed ─────────────────────────────────────────

function LiveOutputsFeed({ recapOutputs = [], topicRows = [], meetingId }) {
  // Build agenda title lookup from topicRows
  const agendaTitleById = {}
  for (const row of topicRows) {
    if (row.agendaItemId) agendaTitleById[row.agendaItemId] = row.title
  }

  // Separate current-session outputs from previous-occurrence carryover.
  // On a parent recurring-meeting page, meetingId is the parent ID.
  // Outputs from the live WS stream (flattenOutputsByAgendaItem) are stamped
  // with sourceMeetingId = parent ID → treated as current.
  // Outputs from getSeriesDetails (historical recaps) are stamped with the
  // child occurrence ID → treated as carryover (action_items / follow_ups only).
  const currentOutputs = []
  const carryoverOutputs = []
  for (const o of recapOutputs) {
    const src = o.sourceMeetingId
    const isCurrent = !src || !meetingId || src === meetingId
    if (isCurrent) {
      currentOutputs.push(o)
    } else if (o.type === 'action_item' || o.type === 'follow_up') {
      carryoverOutputs.push(o)
    }
    // Decisions from other meetings are dropped — they're not relevant to the
    // current session and were previously leaking into "current" on parent pages.
  }
  const allOutputs = currentOutputs

  const TYPE_LABELS = {
    decision: 'Decision',
    action_item: 'Action Item',
    follow_up: 'Follow-up',
  }

  const TYPE_COLORS = {
    decision: 'bg-primary-100 text-primary-700 border-primary-200',
    action_item: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    follow_up: 'bg-amber-100 text-amber-700 border-amber-200',
  }

  return (
    <section className="rounded-2xl border border-accent-200/80 bg-accent-50/40 p-5 shadow-sm">
      <div className="flex items-center gap-2.5 mb-3.5">
        <Zap className="h-5 w-5 text-accent-600" />
        <h4 className="text-base font-bold text-slate-900">Live Meeting Outputs</h4>
        {allOutputs.length > 0 ? (
          <span className="text-xs font-medium text-slate-400">{allOutputs.length}</span>
        ) : null}
      </div>

      {carryoverOutputs.length > 0 ? (
        <div className="mb-3">
          <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">From previous</p>
          <div className="space-y-2">
            {carryoverOutputs.map((o) => (
              <div key={o.id} className="rounded-lg border border-dashed border-slate-200 bg-slate-50/50 px-3 py-2">
                <div className="flex items-start gap-2.5">
                  <span className={`shrink-0 rounded px-1.5 py-0.5 text-xs font-semibold border ${TYPE_COLORS[o.type] ?? 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                    {TYPE_LABELS[o.type] ?? o.type?.replace(/_/g, ' ') ?? 'Output'}
                  </span>
                  <p className="text-xs leading-4 text-slate-600 min-w-0 flex-1">{decodeHtmlEntities(o.summary)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {allOutputs.length > 0 ? (
        <div className="space-y-2.5 max-h-[28rem] overflow-y-auto">
          {allOutputs.map((o) => (
            <div key={o.id} className="rounded-lg border border-slate-200 bg-white px-3 py-2.5">
              <div className="flex items-start gap-2.5">
                <span className={`shrink-0 rounded px-1.5 py-0.5 text-xs font-semibold border ${TYPE_COLORS[o.type] ?? 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                  {TYPE_LABELS[o.type] ?? o.type?.replace(/_/g, ' ') ?? 'Output'}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm leading-5 text-slate-900">{decodeHtmlEntities(o.summary)}</p>
                  <div className="flex items-center gap-3 mt-1">
                    {o.owner ? (
                      <p className="text-xs text-slate-400"><span className="font-medium text-slate-500">Assignee:</span> {o.owner}</p>
                    ) : null}
                    {agendaTitleById[o.agendaItemId] ? (
                      <p className="text-xs text-slate-400 truncate"><span className="font-medium text-slate-500">Topic:</span> {decodeHtmlEntities(agendaTitleById[o.agendaItemId])}</p>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-slate-400">No outputs captured yet</p>
      )}
    </section>
  )
}

// ── Main SharedContextColumn ──────────────────────────────────

function BriefDriverSection({ briefDriverState }) {
  if (!briefDriverState || !briefDriverState.hasBrief) return null
  const goals = Array.isArray(briefDriverState.trackedGoals) ? briefDriverState.trackedGoals : []
  const questions = Array.isArray(briefDriverState.openRequiredQuestions)
    ? briefDriverState.openRequiredQuestions
    : []
  if (goals.length === 0 && questions.length === 0) return null

  const priorityRing = (p) =>
    p === 'high' ? 'border-rose-200 bg-rose-50 text-rose-700'
      : p === 'medium' ? 'border-amber-200 bg-amber-50 text-amber-700'
        : 'border-slate-200 bg-slate-50 text-slate-600'

  const statusBadge = (s) => {
    if (s === 'answered') return { label: 'answered', cls: 'border-emerald-200 bg-emerald-50 text-emerald-700' }
    if (s === 'partially_answered') return { label: 'partial', cls: 'border-amber-200 bg-amber-50 text-amber-700' }
    return { label: 'open', cls: 'border-slate-200 bg-slate-50 text-slate-500' }
  }

  return (
    <section className="space-y-3">
      <h4 className="text-base font-bold text-slate-900">From Your Brief</h4>
      {goals.length > 0 ? (
        <div className="space-y-1.5">
          <p className="text-xs uppercase tracking-wide text-slate-500">Goals tracked</p>
          <div className="flex flex-wrap gap-1.5">
            {goals.map((g, i) => (
              <span
                key={`g-${i}`}
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${priorityRing(g.priority)}`}
                title={`Priority: ${g.priority}`}
              >
                {g.description}
              </span>
            ))}
          </div>
        </div>
      ) : null}
      {questions.length > 0 ? (
        <div className="space-y-1.5">
          <p className="text-xs uppercase tracking-wide text-slate-500">Required questions</p>
          <div className="space-y-1.5">
            {questions.map((q, i) => {
              const badge = statusBadge(q.status)
              return (
                <div key={`q-${i}`} className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                  <p className="text-sm leading-5 text-slate-900">{q.question}</p>
                  <div className="mt-1 flex items-center gap-2">
                    {q.targetParticipantName ? (
                      <span className="text-xs text-slate-400">{q.targetParticipantName}</span>
                    ) : null}
                    <span
                      className={`inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${badge.cls}`}
                    >
                      {badge.label}
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ) : null}
    </section>
  )
}

export default function SharedContextColumn({
  sharedTeamView,
  recapOutputs = [],
  meetingId = null,
  audienceMode = 'internal',
  pulseBarVisible = false,
}) {
  const view = sharedTeamView ?? {}
  const liveQuestions = Array.isArray(view.liveQuestions) ? view.liveQuestions : []
  const topicRows = Array.isArray(view.topicRows) ? view.topicRows : []
  const briefDriverState = view.companionView?.briefDriverState ?? null

  return (
    <div className="flex min-h-0 flex-col">
      {/* Sticky column header */}
      <div className={`sticky ${pulseBarVisible ? 'top-[52px]' : 'top-0'} z-10 flex items-center gap-3 rounded-t-xl border border-slate-200 bg-gradient-to-br from-primary-50/80 to-white/95 px-4 py-3.5 backdrop-blur-sm shadow-sm`}>
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary-100">
          <Users className="h-4 w-4 text-primary-500" />
        </div>
        <h3 className="text-base font-bold text-slate-900">Shared Team View</h3>
      </div>

      <div className="flex flex-col gap-5 rounded-b-xl border border-t-0 border-slate-200 bg-slate-50/30 p-4">
        <BriefDriverSection briefDriverState={briefDriverState} />
        <QuestionsSection liveQuestions={liveQuestions} audienceMode={audienceMode} />
        <LiveOutputsFeed recapOutputs={recapOutputs} topicRows={topicRows} meetingId={meetingId} />
      </div>
    </div>
  )
}
